import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { FileText, Users, CreditCard, ArrowRight } from "lucide-react";

export default function AdminOverview() {
  const [stats, setStats] = useState({ insights: 0, leads: 0, transactions: 0, paid: 0 });
  const [recentLeads, setRecentLeads] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/admin/insights"),
      api.get("/admin/leads"),
      api.get("/admin/transactions"),
    ]).then(([insRes, leadRes, txRes]) => {
      const ins = insRes.data || [];
      const leads = leadRes.data || [];
      const txs = txRes.data || [];
      setStats({
        insights: ins.length,
        leads: leads.length,
        transactions: txs.length,
        paid: txs.filter((t) => t.payment_status === "paid").length,
      });
      setRecentLeads(leads.slice(0, 6));
    }).catch(() => {});
  }, []);

  return (
    <div className="p-6 md:p-10" data-testid="admin-overview-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="bnp-chip bnp-chip-light">Overview</span>
          <h1 className="font-display text-3xl md:text-4xl text-[#04273d] mt-2 font-semibold tracking-tight">Dashboard</h1>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <StatCard label="Blogs & Guidance" value={stats.insights} icon={FileText} testid="stat-insights" />
        <StatCard label="Leads captured" value={stats.leads} icon={Users} testid="stat-leads" />
        <StatCard label="Transactions" value={stats.transactions} icon={CreditCard} testid="stat-transactions" />
        <StatCard label="Paid customers" value={stats.paid} icon={CreditCard} testid="stat-paid" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 bnp-card p-6" data-testid="admin-recent-leads-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold text-[#04273d]">Recent leads</h2>
            <Link to="/admin/leads" className="text-sm text-[#447694] hover:text-[#04273d] inline-flex items-center gap-1">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          {recentLeads.length === 0 ? (
            <p className="text-sm text-[#447694] py-6">No leads captured yet.</p>
          ) : (
            <ul className="divide-y divide-[#9ac7f7]/25">
              {recentLeads.map((l) => (
                <li key={l.id} className="py-3 flex items-center justify-between gap-3 text-sm">
                  <div className="min-w-0">
                    <p className="text-[#04273d] truncate">{l.email}</p>
                    <p className="font-mono-brand text-[10px] text-[#447694] tracking-wider uppercase mt-0.5">{l.source} {l.business_name ? `· ${l.business_name}` : ""}</p>
                  </div>
                  <span className="font-mono-brand text-[10px] text-[#447694] tracking-wider uppercase shrink-0">
                    {new Date(l.created_at || Date.now()).toLocaleDateString("en-GB")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bnp-card p-6 bg-[#04273d] text-white border-0" data-testid="admin-quick-actions-card">
          <h2 className="font-display text-lg font-semibold">Quick actions</h2>
          <div className="space-y-2 mt-4">
            <Link to="/admin/insights/new" className="flex items-center justify-between px-4 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-sm">
              Write a new article <ArrowRight size={14} />
            </Link>
            <Link to="/admin/leads" className="flex items-center justify-between px-4 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-sm">
              Export leads as CSV <ArrowRight size={14} />
            </Link>
            <Link to="/admin/transactions" className="flex items-center justify-between px-4 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-sm">
              Review transactions <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <div className="bnp-card p-5" data-testid={testid}>
      <div className="h-9 w-9 rounded-xl bg-[#71edc9]/30 flex items-center justify-center">
        <Icon size={16} className="text-[#04273d]" />
      </div>
      <p className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase mt-3">{label}</p>
      <p className="font-display text-3xl text-[#04273d] mt-1 font-bold">{value}</p>
    </div>
  );
}
